import { jwtDecode } from 'jwt-decode';

export const REGISTER_SESSION_EVENT = 'exreg-register-session';
const switchGuards = new Set();
let tabToken = typeof localStorage === 'undefined' ? null : localStorage.getItem('token');

export function currentRegisterToken() { return tabToken; }

export function registerContextHeaders(token) {
  try {
    const payload = jwtDecode(token);
    if (typeof payload.database_name !== 'string' || !payload.database_name) return {};
    const version = payload.register_context_version ?? 0;
    if (!Number.isInteger(version) || version < 0) return {};
    return { 'X-Register-Name': payload.database_name, 'X-Register-Version': String(version) };
  } catch (_) { return {}; }
}

export function registerContextChangedError() {
  const error = new Error('Your account or register changed. Open the current register before continuing.');
  error.code = 'REGISTER_CONTEXT_CHANGED';
  error.response = { status: 409, data: { code: error.code, message: error.message } };
  return error;
}

export function isRegisterSessionCurrent(token) {
  return Boolean(token) && token === tabToken && token === localStorage.getItem('token');
}

export function installRegisterSession(token) {
  if (typeof token !== 'string' || !token) throw new Error('The server did not return a valid session.');
  localStorage.setItem('token', token);
  tabToken = token;
  window.dispatchEvent(new Event(REGISTER_SESSION_EVENT));
}

export function clearRegisterSession() {
  localStorage.removeItem('token');
  tabToken = null;
  window.dispatchEvent(new Event(REGISTER_SESSION_EVENT));
}

// Called only after the user explicitly leaves the old, blocked workspace.
export function adoptStoredRegisterSession() {
  tabToken = localStorage.getItem('token');
  window.dispatchEvent(new Event(REGISTER_SESSION_EVENT));
}

export function registerSwitchGuard(guard) {
  switchGuards.add(guard);
  return () => switchGuards.delete(guard);
}

export function confirmRegisterSwitch() {
  return [...switchGuards].every(guard => guard());
}

function getHeader(headers, name) {
  if (typeof headers?.get === 'function') return headers.get(name);
  return Object.entries(headers || {}).find(([key]) => key.toLowerCase() === name.toLowerCase())?.[1];
}

function accountVerificationRequest(url) {
  return /\/api\/(?:auth|users)\/(?:login(?:\/2fa)?|me|register|forgot-password|reset-password)(?:[/?]|$)/.test(url);
}

// Keep this tab's accepted session distinct from shared localStorage. An old
// tab must never attach a newer token to its existing forms and equipment IDs.
export function scopeRegisterRequest(config) {
  const url = String(config.url || '');
  const authorization = getHeader(config.headers, 'Authorization');
  const token = typeof authorization === 'string' ? authorization.replace(/^Bearer\s+/i, '') : null;
  if (!token || accountVerificationRequest(url)) return config;
  if (!isRegisterSessionCurrent(token)) throw registerContextChangedError();
  config.headers = config.headers || {};
  const scopedHeaders = registerContextHeaders(token);
  for (const [key, value] of Object.entries(scopedHeaders)) {
    const supplied = getHeader(config.headers, key);
    if (supplied !== undefined && supplied !== null && String(supplied) !== value) throw registerContextChangedError();
    config.headers[key] = value;
  }
  config.headers['X-Client'] = 'web';
  config.exregSessionToken = token;
  return config;
}

export function installRegisterRequestSafety(client) {
  const requestId = client.interceptors.request.use(scopeRegisterRequest);
  const responseId = client.interceptors.response.use(response => {
    const token = response.config?.exregSessionToken;
    if (token && !isRegisterSessionCurrent(token)) throw registerContextChangedError();
    return response;
  });
  return () => {
    client.interceptors.request.eject(requestId);
    client.interceptors.response.eject(responseId);
  };
}
